import NiceModal from "@ebay/nice-modal-react";
import { canv } from "@/canv/global";
import { getViewport } from "@/canvasController/helpers";
import type { ExtendsOfElementBase } from "@/canvasController/types";
import {
    FILE_FORMAT_BY_TYPE,
    FILE_FORMATS,
    MAX_FILE_SIZE,
    MAX_IMAGE_HEIGHT,
    MAX_IMAGE_WIDTH,
} from "@/constants";
import { CommentWidget } from "@/elements/comment";
import { frameWidget } from "@/elements/frame";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import type { Group } from "@/group";
import { ReloadModal } from "@/interface/ReloadModal";
import type { EntityBaseState } from "@/shared/types/entityState";

export function isBrowser() {
    if (window !== undefined && "forceIsBrowser" in window && window.forceIsBrowser) {
        return true;
    }

    // Check if the environment is Node.js
    if (typeof process === "object" && typeof require === "function") {
        return false;
    }

    // Check if the environment is a
    // Service worker
    //@ts-ignore
    if (typeof importScripts === "function") {
        return false;
    }

    // Check if the environment is a Browser
    if (typeof window === "object") {
        return true;
    }
}

export const isGuestToken = (token: string) => {
    return ["", "guest"].includes(token);
};

export function createBlobFromString(message: string) {
    return new Blob([message], { type: "application/json" });
}

export function createWidgetsDumpsBlobFromString(message: string) {
    return new Blob([message], { type: "text/plain" });
}

export function randomIntFromInterval(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export function getTarget(e: Event, className: string) {
    let elem: any = null;
    const path = e.composedPath();

    for (let i = 0; i < path.length; i++) {
        //@ts-ignore
        const classesList = path[i].classList;

        if (classesList === undefined) return elem;

        if (classesList.contains(className)) {
            elem = path[i];
            break;
        }
    }

    return elem;
}

export function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

JSON.safeStringify = (obj) => {
    return JSON.stringify(obj, function replacer(key, value) {
        if (key === "parent") return undefined;
        else if (key === "parentItem") return undefined;
        else return value;
    });
};

export function lightOrDark(color) {
    if (color === "transparent") {
        return "light";
    }

    if (color === undefined) {
        return "light";
    }

    // Variables for red, green, blue values
    let r, g, b, hsp;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {
        // If RGB --> store the red, green, blue values in separate variables
        color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

        r = color[1];
        g = color[2];
        b = color[3];
    } else {
        // If hex --> Convert it to RGB: http://gist.github.com/983661
        color = +("0x" + color.slice(1).replace(color.length < 5 && /./g, "$&$&"));

        r = color >> 16;
        g = (color >> 8) & 255;
        b = color & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

    // Using the HSP value, determine whether the color is light or dark
    if (hsp > 150) {
        return "light";
    } else {
        return "dark";
    }
}

export const generateUID = () => {
    let d = Date.now(),
        d2 = (performance?.now && performance.now() * 1000) || 0;
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        let r = Math.random() * 16;
        if (d > 0) {
            r = ((d + r) % 16) | 0;
            d = Math.floor(d / 16);
        } else {
            r = ((d2 + r) % 16) | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x7) | 0x8).toString(16);
    });
};

export function matrixTranslate(x: number, y: number) {
    canv.m[4] += x + canv.m[2] * y;
    canv.m[5] += canv.m[1] * x + y;
}

export function reverseTransformPoint(px: number, py: number) {
    const x = px;
    const y = py;

    px = x + canv.m[4];
    py = y + canv.m[5];

    px = px / canv.dpr;
    py = py / canv.dpr;

    return {
        x: px,
        y: py,
    };
}

/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {boolean} [defaultValue=false]
 * @returns {boolean}
 */
export const getBoolProperty = (key, obj, defaultValue = false) => {
    try {
        if (obj[key] === "true") {
            return true;
        }
        if (obj[key] === "false") {
            return false;
        }
        return Boolean(obj[key]);
    } catch (e) {
        console.log(`trying to get bool by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};
/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {number} [defaultValue=0]
 * @returns {number}
 */
export const getNumberProperty = (key, obj, defaultValue = 0) => {
    try {
        return Number(obj[key]);
    } catch (e) {
        console.log(`trying to get number by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};
/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {string} [defaultValue=""]
 * @returns {string}
 */
export const getStringProperty = (key, obj, defaultValue = "") => {
    try {
        return escapeString(obj[key]);
    } catch (e) {
        console.log(`trying to get string by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};

/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {string} [defaultValue=""]
 * @returns {string}
 */
export const getColorProperty = (key, obj, defaultValue = "") => {
    try {
        return escapeString(obj[key]);
    } catch (e) {
        console.log(`trying to get color by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};

export const normalizeImageUrl = (src: string) => {
    if (src.startsWith("data:image")) {
        // base64
        return src;
    }
    const [, path] = src.split("/api/");
    if (path) {
        return `${canv.host}/api/${path}`;
    }
    return src;
};

export function setClick(className: string, func: () => void) {
    const item = document.getElementsByClassName(className)[0];

    if (item !== undefined) {
        item.addEventListener("mousedown", func, false);
    }
}

export async function MakeRequest<T extends {}>(
    handler: string,
    requestData: T,
    noResponse = false,
    niceToHave = false,
    keepAlive = false,
) {
    let result: Response;

    try {
        const keepalive = keepAlive ? { keepalive: true } : {};
        result = await fetch(canv.host + handler, {
            ...keepalive,
            method: "POST",
            body: JSON.stringify(requestData),
        });

        if (!result.ok) {
            if (niceToHave) return undefined;

            const logData =
                `HTTP request fail [not 200 code] | from board | ` +
                canv.host +
                handler +
                ` | data: ` +
                JSON.stringify(requestData);
            logException(new Error(logData));
            throw 1;
        }
    } catch (e) {
        if (niceToHave) return undefined;

        logException(e);
        throw 1;
    }

    if (noResponse) {
        return {};
    }

    let resJson: any;

    try {
        resJson = await result.json();
    } catch (e) {
        if (niceToHave) return undefined;

        logException(e);
        throw 1;
    }

    return resJson;
}

export function drawRoundedRect(x: number, y: number, w: number, h: number, r: number) {
    if (w < 2 * r) r = w / 2;
    if (h < 2 * r) r = h / 2;

    canv.ctx.beginPath();
    canv.ctx.moveTo(x + r, y);
    canv.ctx.arcTo(x + w, y, x + w, y + h, r);
    canv.ctx.arcTo(x + w, y + h, x, y + h, r);
    canv.ctx.arcTo(x, y + h, x, y, r);
    canv.ctx.arcTo(x, y, x + w, y, r);
    canv.ctx.closePath();
}

export function isOverRect(startX: number, startY: number, width: number, height: number) {
    const pt = canv.actionManager.getActionPoint();

    const x = pt.x;
    const y = pt.y;

    if (x >= startX && x <= startX + width && y >= startY && y <= startY + height) {
        return true;
    }

    return false;
}

export function easeInSine(x: number) {
    return 1 - Math.cos((x * Math.PI) / 2);
}

export const isImageSizeValid = (imageSize: number) => imageSize <= MAX_FILE_SIZE;
export const isImageResolutionValid = (width: number, height: number) => {
    return width * height <= MAX_IMAGE_WIDTH * MAX_IMAGE_HEIGHT;
};
export const isFileFormatValid = (file: File) => {
    if (isKeynoteOfWindowFile(file)) return true;

    return FILE_FORMATS.includes(file.type);
};

/**
 * @param {Error | unknown} error
 */
export function logException(error) {
    canv.captureException(error);
    console.log(error);
    NiceModal.show(ReloadModal);
}

const ESCAPE_MAP = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    // '/': '&#x2F;',
};

const ESCAPE_REGEX = /[&<>"']/g;
const EVENT_HANDLERS_REGEX =
    /\b(vbscript|data:text\/html|javascript|onclick|onload|onerror|ondblclick|onmousedown|onmousemove|onmouseover|onmouseout|onkeypress|onkeydown|onkeyup|onsubmit|onreset|onfocus|onblur|onchange|onselect|oncut|oncopy|onpaste|oncontextmenu|onwheel|ontouchstart|ontouchend|ontouchmove|ongesturestart|ongesturechange|ongestureend)(?=\s*=|)/gi;

export const escapeString = (str) => {
    if (typeof str !== "string") return str;

    let safeStr = str.replace(ESCAPE_REGEX, (c) => ESCAPE_MAP[c]);
    safeStr = safeStr.replace(EVENT_HANDLERS_REGEX, "");

    return safeStr;
};

export const unescapeString = (str) => {
    if (typeof str !== "string") return str;

    return str.replace("&amp;", "&");
};

// Функции чтобы рисовать arrowEnd для arrow (что именно считают не знаю)

type Pt = { x: number; y: number };

export function getCubicBezierXYatT(
    startPt: Pt,
    controlPt1: Pt,
    controlPt2: Pt,
    endPt: Pt,
    T: number,
) {
    const x = CubicN(T, startPt.x, controlPt1.x, controlPt2.x, endPt.x);
    const y = CubicN(T, startPt.y, controlPt1.y, controlPt2.y, endPt.y);

    return { x: x, y: y };
}

// cubic helper formula at T distance
export function CubicN(T: number, a: number, b: number, c: number, d: number) {
    const t2 = T * T;
    const t3 = t2 * T;

    return (
        a +
        (-a * 3 + T * (3 * a - a * T)) * T +
        (3 * b + T * (-6 * b + b * 3 * T)) * T +
        (c * 3 - c * 3 * T) * t2 +
        d * t3
    );
}

export function easeOutCubic(x: number) {
    return 1 - (1 - x) ** 3;
}

export function isSeveralEntityTypesInSelected(selected: Group[]) {
    let prevCodeName = "";

    for (let i = 0; i < selected.length; i++) {
        let currentCodeName = "";

        if (selected[i].isItemWrapper()) {
            currentCodeName = selected[i].onlyChild()?.constructorName as string;
        } else {
            currentCodeName = "group";
        }

        if (prevCodeName !== "" && prevCodeName !== currentCodeName) {
            return true;
        } else {
            prevCodeName = currentCodeName;
        }
    }
}

export function debounce(func, delay) {
    let timeoutId;

    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            //@ts-ignore
            func.apply(this, args);
        }, delay);
    };
}

/**
 *
 * @param {number} x
 * @param {number} y
 * @param {object} widget
 * @returns {boolean}
 */
export function isOnScreenAfterTranslate(x = 0, y = 0, widget: ExtendsOfElementBase) {
    const viewport = getViewport();

    return (
        viewport.viewportStartX <= widget.startX + widget.width + x &&
        viewport.viewportStartY <= widget.startY + widget.height + y &&
        viewport.viewportStartX + viewport.viewportWidth >= widget.startX + x &&
        viewport.viewportStartY + viewport.viewportHeight >= widget.startY + y
    );
}

export function isOnScreen(widget: Group) {
    const viewport = getViewport();

    return (
        viewport.viewportStartX <= widget.startX + widget.width &&
        viewport.viewportStartY <= widget.startY + widget.height &&
        viewport.viewportStartX + viewport.viewportWidth >= widget.startX &&
        viewport.viewportStartY + viewport.viewportHeight >= widget.startY
    );
}

export function updateCommentsMessages(commentsActions: any[], isApplied: boolean) {
    const commentsByUid = canv.canvasController.items.reduce((acc, cur) => {
        const isCommentWidget = cur.isItemWrapper() && cur.children[0] instanceof CommentWidget;
        if (isCommentWidget) {
            acc[cur.children[0].UID] = cur.children[0];
        }

        return acc;
    }, {});

    commentsActions.forEach((action) => {
        const comment = commentsByUid[action.UID];

        if (action.type === "propertyBuild" && action.action === "add") {
            if (isApplied && comment) {
                comment.addMessage(action.property);
            }
        }

        if (action.type === "propertyBuild" && action.action === "remove") {
            if (isApplied && comment) {
                comment.removeMessage(action.property.UID);
            }
        }
    });
}

export function getSanitizedComment(comment: EntityBaseState) {
    return {
        entityType: getStringProperty("entityType", comment),
        UID: getStringProperty("UID", comment),
        parentUID: getStringProperty("parentUID", comment),
        startX: getNumberProperty("startX", comment),
        startY: getNumberProperty("startY", comment),
        isResolved: getBoolProperty("isResolved", comment),
        accountName: getStringProperty("accountName", comment),
        createdAt: getStringProperty("createdAt", comment),
        text: getStringProperty("text", comment),
        token: getStringProperty("token", comment),
        messages: Array.isArray(comment.messages)
            ? comment.messages.map((message) => ({
                  UID: getStringProperty("UID", message),
                  createdAt: getStringProperty("createdAt", message),
                  token: getStringProperty("token", message),
                  accountName: getStringProperty("accountName", message),
                  text: getStringProperty("text", message),
              }))
            : [],
        updatedAt: getStringProperty("updatedAt", comment),
    };
}

export function getSanitizedGroup(group: EntityBaseState) {
    return {
        UID: getStringProperty("UID", group),
        parentUID: getStringProperty("parentUID", group),
        itemWrapper: getBoolProperty("itemWrapper", group),
        entityType: getStringProperty("entityType", group),
        indexByParent: getStringProperty("indexByParent", group),
        arrows: group.arrows.map((arrow) => ({
            point: getStringProperty("point", arrow),
            arrowItemUID: getStringProperty("arrowItemUID", arrow),
            place: getStringProperty("place", arrow),
            share: getNumberProperty("share", arrow, 0.5),
        })),
        angle: getNumberProperty("angle", group),
        isLocked: getBoolProperty("isLocked", group),
    };
}

export function getSanitizedMessage(message) {
    return {
        UID: getStringProperty("UID", message),
        createdAt: getStringProperty("createdAt", message),
        token: getStringProperty("token", message),
        accountName: getStringProperty("accountName", message),
        text: getStringProperty("text", message),
    };
}

export function getSanitizedEventInfo(event) {
    const type = getStringProperty("type", event);

    if (type === "build") {
        return { type, action: getStringProperty("action", event) };
    }

    if (type === "property") {
        return {
            type,
            UID: getStringProperty("UID", event),
            fixType: getStringProperty("fixType", event),
        };
    }

    if (type === "propertyBuild") {
        return {
            type,
            action: getStringProperty("action", event),
            UID: getStringProperty("UID", event),
            fixType: getStringProperty("fixType", event),
        };
    }

    return event;
}

function getValueAtPath(obj, path) {
    return path.reduce((acc, key) => acc?.[key], obj);
}

type StackItem = {
    value: any;
    path: string[];
    parent: any;
    keyInParent: string | number | null;
    container: any;
};

export function walkDeepSanitize(input) {
    const root = input;
    const isRootArray = Array.isArray(input);
    const output = isRootArray ? [] : {};

    const stack: StackItem[] = [
        {
            value: input,
            path: [],
            parent: null,
            keyInParent: null,
            container: output,
        },
    ];

    while (stack.length > 0) {
        const stackItem = stack.pop();
        const { value, path, parent, keyInParent } = stackItem as StackItem;

        if (Array.isArray(value)) {
            const arr = [];
            if (parent && keyInParent !== null) parent[keyInParent] = arr;

            value.forEach((item, index) => {
                stack.push({
                    value: item,
                    path: [...path, String(index)],
                    parent: arr,
                    keyInParent: index,
                    container: arr,
                });
            });

            if (!parent && path.length === 0) {
                value.forEach((_, i) => {
                    output[i] = undefined;
                });
                value.forEach((item, index) => {
                    stack.push({
                        value: item,
                        path: [String(index)],
                        parent: output,
                        keyInParent: index,
                        container: output,
                    });
                });
            }
        } else if (typeof value === "object" && value !== null) {
            const obj = {};
            if (parent && keyInParent !== null) parent[keyInParent] = obj;

            Object.entries(value).forEach(([key, val]) => {
                stack.push({
                    value: val,
                    path: [...path, key],
                    parent: obj,
                    keyInParent: key,
                    container: obj,
                });
            });

            if (!parent && path.length === 0) {
                Object.assign(output, obj);
                Object.entries(value).forEach(([key, val]) => {
                    stack.push({
                        value: val,
                        path: [key],
                        parent: output,
                        keyInParent: key,
                        container: output,
                    });
                });
            }
        } else {
            const parentObj = getValueAtPath(root, path.slice(0, -1));
            const key = path[path.length - 1];
            let result;

            if (typeof value === "boolean") {
                result = getBoolProperty(key, parentObj);
            } else if (typeof value === "string") {
                result = getStringProperty(key, parentObj);
            } else if (typeof value === "number") {
                result = getNumberProperty(key, parentObj);
            }

            if (parent && keyInParent !== null) {
                parent[keyInParent] = result;
            } else if (path.length === 1) {
                output[key] = result;
            }
        }
    }

    return output;
}

export function isVoteCreateEvent(event) {
    if (event.type === "build" && event.action === "create") {
        return event.entities[0].entityType === "vote";
    }

    return false;
}

export function isVoteEndedEvent(event) {
    if (event.type === "property" && event.state.isResolved) {
        return canv.canvasController.activeVote?.UID === event.UID;
    }

    return false;
}

export function isOnlyCommentWidget() {
    return (
        canv.canvasController.selected.length === 1 &&
        canv.canvasController.selected[0].isItemWrapper() &&
        canv.canvasController.selected[0].children[0] instanceof CommentWidget
    );
}

export const checkMaxNum = (num: number) => {
    return num > Number.MAX_SAFE_INTEGER;
};

export const commentResolveIsDisabled = () => {
    if (canv.isGuest()) {
        return true;
    }

    return !canv.isBoardModeEditOrOwner() && !canv.isBoardModeEditMini();
};

export const messageActionsIsDisabled = commentResolveIsDisabled;

const blobToDataUrl = (b) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(b);
    });
};

export const prepareImage = async (blob: Blob): Promise<HTMLImageElement> => {
    const fn = async (resolve) => {
        const img = new Image();
        const objectURL = (await blobToDataUrl(blob)) as string;

        img.src = objectURL;
        img.crossOrigin = "anonymous";
        img.onload = () => {
            resolve(img);
        };
        img.onerror = () => {
            resolve();
        };
    };

    return new Promise(fn);
};

export const isKeynoteOfWindowFile = (file: File) => {
    return file.name.toLowerCase().endsWith(".key");
};

export const isTypeForDocumentViewerWidget = (file: File) => {
    if (isKeynoteOfWindowFile(file)) return true;

    return ["pdf", "doc", "docx", "txt", "rtf", "odt", "ppt", "pptx", "key", "html"].includes(
        FILE_FORMAT_BY_TYPE[file.type],
    );
};

export const DEFAULT_BLACK_COLOR = "#2C2D32";
export const DEFAULT_WHITE_COLOR = "#FAFAFA";

export const getContrastColor = (hexcolor: string) => {
    if (hexcolor.startsWith("#")) {
        hexcolor = hexcolor.slice(1);
    }
    if (hexcolor.length !== 6) {
        console.log("wrong hex color", hexcolor);
        return DEFAULT_BLACK_COLOR;
    }

    const r = parseInt(hexcolor.substring(0, 2), 16);
    const g = parseInt(hexcolor.substring(2, 4), 16);
    const b = parseInt(hexcolor.substring(4, 6), 16);

    const yiq = (r * 299 + g * 587 + b * 114) / 1000;

    return yiq >= 128 ? DEFAULT_BLACK_COLOR : DEFAULT_WHITE_COLOR;
};

export const getOSSymbolInfo = () => {
    let actionSymbol = "Ctrl";
    let actionSymbolName = "Ctrl";
    let altSymbol = "Alt";

    let os = "Windows";

    if (navigator.appVersion.indexOf("Win") !== -1) os = "Windows";
    if (navigator.appVersion.indexOf("Mac") !== -1) os = "MacOS";
    if (navigator.appVersion.indexOf("X11") !== -1) os = "UNIX";
    if (navigator.appVersion.indexOf("Linux") !== -1) os = "Linux";

    if (os === "MacOS") {
        actionSymbol = "&#8984;";
        actionSymbolName = "Cmd";
        altSymbol = "&#8997;";
    }

    return {
        actionSymbol,
        actionSymbolName,
        altSymbol,
    };
};

export function isTimerCreateEvent(event) {
    if (!event) return false;
    if (event.type === "build" && event.action === "create") {
        return event.entities[0].entityType === "timer";
    }

    return false;
}

export const isTimerPropertyEvent = (messageData) => {
    const entity = messageData[0];
    if (!entity) return false;
    return entity.state && entity.entityType === "timer";
};

export function isPresentationStartEvent(event) {
    return (
        event.type === "property" && event.fixType === "startPresentation" && event.state?.isActive
    );
}

export function isPresentationStopEvent(event) {
    return (
        event.type === "property" && event.fixType === "stopPresentation" && !event.state?.isActive
    );
}

export function relinkPresentations(newGroups: Group[]) {
    const frameByTrailUID = new Map();
    const presentationByTrailUID = new Map();

    // Строим индексы
    for (const group of newGroups) {
        if (!group.isItemWrapper()) continue;

        const child = group.children[0];

        if (child instanceof frameWidget) {
            frameByTrailUID.set(group.trailUID, group);
        }

        if (child instanceof PresentationWidget) {
            presentationByTrailUID.set(child.trailUID, group);
        }
    }

    // Обновляем slides у презентаций
    for (const group of newGroups) {
        if (!group.isItemWrapper()) continue;

        const child = group.children[0];

        if (child instanceof PresentationWidget) {
            const presentation = child;

            presentation.slides = presentation.slides.map((slide) => {
                const matched = frameByTrailUID.get(slide.frameUID);
                if (matched) {
                    slide.frameUID = matched.UID;
                }
                return slide;
            });
        }
    }

    // Обновляем связи frame внутри presentation
    for (const group of newGroups) {
        if (!group.isItemWrapper()) continue;

        const child = group.children[0];

        if (child instanceof frameWidget) {
            const frame = child;

            if (frame.presentationUID) {
                const presentationGroup = presentationByTrailUID.get(frame.presentationUID);

                frame.presentationUID = presentationGroup?.children[0].UID ?? null;
                frame.presentationGroupUID = null;
            } else {
                frame.presentationUID = null;
                frame.presentationGroupUID = null;
            }
        }
    }
}
